import { Toast, Dialog } from 'mand-mobile'
import {
  AxiosStatic,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios'
import { useI18n } from '~/utils/i18n'
import {
  useStore,
  createPropertiesByState,
  useState,
} from '~/utils/store'
import { relogin } from '@/utils/login'
import { TranslateResult } from 'vue-i18n'
import log from '~/utils/log'
import {
  SAFETY_INTERCEPT_CODE,
  NODE_INTERFACE_STATUS,
  AGREEMENT_NOT_SIGNED_CODE,
} from '~/constants/api'
import { useRouter } from '~/utils/router'
import { AxiosPromise } from '@didi/dajax'
import { getExternalChannel } from '@/utils/common'
import { trackApiEvent } from '@didi/error-base'
import { createAxiosInterceptor } from '@didi/wsgsdk-corejs3'
import { IUserStoreState } from '@/store/modules/user'
import { getBaseURL } from './getBaseURL'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const axios: AxiosStatic = require('@didi/dajax').default

export interface API_ERROR {
  code: number | string
  message: string
  error: IAxiosError
}

interface IAxiosError extends AxiosError {
  /**
   * 重写config类型
   */
  config: IRequestConfig
}

/**
 * 请求参数
 */
export interface IRequestConfig extends AxiosRequestConfig {
  /**
   * 是否启用加载动画
   */
  loading?: boolean

  /**
   * 是否使用提示异常信息
   */
  toastError?: boolean

  /**
   * 是否使用弹窗异常信息
   */
  dialogError?: boolean

  /**
   * 请求超时后是否报错
   */
  timeoutToast?: boolean

  /**
   * raven 跟踪请求
   */
  raven?: {
    done?: (...args: any[]) => any
  }

  /**
   * omega接口数据上报
   */
  trackApiDone?: (
    success: boolean,
    res: IRequestResponse | undefined,
    data?: API_ERROR
  ) => void
}

/**
 * 请求响应
 */
export interface IRequestResponse extends AxiosResponse {
  /**
   * 重写config类型
   */
  config: IRequestConfig
}

export const ax = axios.create({
  baseURL: `${window.location.origin}`,
  needEncrypt: false,
} as any)

/**
 * 通用请求参数
 */
let globalConfig: IRequestConfig = {
  data: {},
}

// 绑定通用请求参数
globalConfig.data = Object.defineProperties(
  {},
  {
    /**
     * 用户系统和端上信息
     */
    os: createPropertiesByState('rcext.os'),
    /**
     * native环境
     */
    applyChannel: createPropertiesByState(
      'rcext.applyChannel'
    ),
    /**
     * 产品ID
     */
    productId: createPropertiesByState('vendor.productId'),
    /**
     * 渠道ID
     */
    fundsChannelId: createPropertiesByState(
      'vendor.fundsChannelId'
    ),
    /**
     * 系统标识
     */
    userFlag: createPropertiesByState('home.userFlag'),
    /**
     * 用户当前所处申请流程
     */
    applyType: createPropertiesByState('home.applyType'),
  }
)

/**
 * 配置全局通用参数
 * @param config
 */
export const installRequestConfig = (
  config: IRequestConfig
) => {
  globalConfig = config
}

ax.interceptors.request.use(config => {
  if(!config.headers?.['Content-Type'] && typeof config.data === 'object'){
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
  }
	createAxiosInterceptor({placement: 'query'})(config)
	return config
})

ax.interceptors.request.use((config: IRequestConfig) => {
  const { url, baseURL , params, data }  = config

  /**
   * 挂载接口错误上报方法
  */
  config.trackApiDone = trackApiEvent(
      // 链接配置
      {
        url, 
        baseURL
      },
      // 接口参数
      data || params,
      {
        // errCode: string[] 错误码key（返回体中data的错误码属性）,响应体中 错误码的 key 名称，可以是一个，也可以是多个
        errCode: ["errorCode", "code", "errno"],
        // errMsg: string[] 错误信息key（返回体中data的错误信息属性）
        errMsg: ["errorMsg", "errmsg", "message"],
        // 接口成功码
        successCode: [0, 10000, 29999],
        // (非必需) headerTraceIdKeys: string[] 如果需要从 header 中获取 trace id。可以提供对应的 header 名称
        headerTraceIdKeys: ["trace-id", "colored-didi-header-rid"],
        // (非必需) dataTraceIdKeys: string[] 如果需要从 response.data 中获取 trace id。可以提供对应的 字段的名称.
        dataTraceIdKeys: ["traceId", "trace_id"],
        // headerTraceIdKeys 和 dataTraceIdKeys 都不是必须的，按照自己的实际情况填写即可
      }
    )

  // 请求超时loading 默认关闭 undefined
  if (config.loading) {
    setTimeout(() => {
      if (config.loading) Toast.loading('')
    }, 500)
  }

  // eslint-disable-next-line no-prototype-builtins
  if (!config.hasOwnProperty('needEncrypt')) {
    (config as any).needEncrypt = true
  }

  /**
   * 如果传递是数据是 formdata，则把公共参数追加到 formdata 里
   * 否则会被公共参数覆盖掉从而导致数据不正确
   */
  if (config.data && config.data instanceof FormData) {
    Object.keys(globalConfig.data).forEach((key) => {
      if (config.data[key] !== null) {
        config.data.append(key, globalConfig.data[key])
      }
    })
  } else {
    config.data = {
      ...globalConfig.data,
      ...config.data,
    }
  }
  const { token } = useState<IUserStoreState>('user', ['token'])
  config.headers = {
    ...(config.headers || {}),
    'didi-fintech-header-content': JSON.stringify({
      token,
      fintechToken: '',
    }),
  }
  return config
})

/**
 * raven.done 上报的 data 字段必须是 errorCode 和 errorMsg，
 * 数据源会使用这两个字段清洗数据
 */
ax.interceptors.response.use(
  (response: IRequestResponse) => {
    const { config, data } = response

    if (config.loading) {
      Toast.hide()
      config.loading = false
    }

    if (config.trackApiDone) {
      config.trackApiDone(true, response)
    }

    // 黑产安全拦截
    if (data?.errorCode === SAFETY_INTERCEPT_CODE) {
      useRouter()?.replace({
        name: 'safety_intercept',
      })
      // 命中黑产安全拦截后无需toast,直接reject
      return Promise.reject(data)
    }

    // 降级信息获取(code：29999，也当作请求成功，但按照降级处理)
    if (data?.errorCode === 29999) {
      useStore().commit(
        'service-demotion/saveServiceDemotion',
        true
      )

      log.error('service_demotion_err', {
        url: config.url,
        error_code: data?.errorCode,
      })
    }
    //首页加载失败
    if (data?.errorCode === NODE_INTERFACE_STATUS.BREAKER_OPEN) {
      useStore().commit(
        'service-demotion/saveServiceBreaker',
        response.data.data.entireSiteConfig.value
      )
      // 不展示错误toast
      config.toastError = false
    }

    // getNextPage 协议未签署由流程补签后重试，无需全局toast
    const isGetNextPage = config.url?.includes('/applyCard/current/getNextPage')
    const responseCode = Number(data?.errorCode ?? data?.code)
    if (isGetNextPage && responseCode === AGREEMENT_NOT_SIGNED_CODE) {
      config.toastError = false
      return Promise.reject(data)
    }

    // 请求成功
    if (data?.errorCode === 10000 ||
      data?.errorCode === 0 ||
      data?.errorCode === 29999 ||
      data?.errno === 0
    ) {
      return Promise.resolve(data)
    }

    // passport过期，统一拦截
    if (data?.code === 26370296117318 || data?.errorCode === 26370296117318 || data?.code === NODE_INTERFACE_STATUS.PASSPORT_EXPIRED ) {
      log.error('common_passport_err', {
        url: config.url,
        err: data,
      })
      relogin()
      return Promise.reject(data)
    }

    /**
     * 全局异常处理提示
     * Dialog > Toast
     */
    const message =
      data?.errorMsg ||
      (useI18n().t(
        'financial.loan.freya.common.error.retry_later'
      ) as string)

    if (config.dialogError === true) {
      Dialog.alert({
        content: message,
        confirmText: useI18n().t(
          'financial.loan.freya.common.ok_btn'
        ) as string,
      })
    } else if (config.toastError !== false) {
      Toast({
        content: message,
        duration: 3000,
        icon: 'warn',
      })
    }

    return Promise.reject(data)
  },
  (error) => {
    const config = (error?.config || {}) as IRequestConfig
    const request = error?.request || {}
    const code = error?.code
    let message: string | TranslateResult =
      error?.message || ''
    let timeoutFlag = false
    let networkErrorFlag = false

    if(config.trackApiDone) {
      try {
        config.trackApiDone(false, undefined, {
          code,
          message: typeof message === 'string' ? message : String(message || ''),
          error,
        });
      } catch (e) {
        // Ignore reporting failures to preserve the original request error.
      }
    }
    if (
      error.code === 'ECONNABORTED' &&
      error.message?.indexOf('timeout') !== -1
    ) {
      timeoutFlag = true
      log.error('request_timeout_err', {
        url: request.url || config.url,
        err: error,
      })
      if (config.timeoutToast !== false) {
        message = useI18n().t(
          'financial.loan.freya.common.error.request_timeout'
        ) as string
        Toast({
          content: message,
          duration: 2000,
          icon: 'warn',
        })
      }
    }

    // 断网错误
    if (error.message?.indexOf('Network Error') !== -1) {
      log.error('request_network_err', {
        url: request.url || config.url,
        error,
      })
      networkErrorFlag = true
      message = useI18n().t(
        'financial.loan.freya.loan.error.no_signal.desc'
      ) as string
      Toast({
        content: message,
        duration: 2000,
        icon: 'warn',
      })
    }

    // 所有错误都往上抛
    throw {
      ...error,
      message,
      timeoutFlag,
      networkErrorFlag,
    }
  }
)

/**
 * http get
 */
export const get = <T = any>(
  uri: string,
  data?: Record<string, unknown>
): AxiosPromise<T> => {
  return ax.get(uri, {
    params: data,
  })
}

/**
 * 网关层调用
 * 网关配置的apollo地址: http://ab.us.intra.xiaojukeji.com/conf/46/14652/21220/801076
 * mx: https://ab-mx.intra.didipay.com/conf/46/14652/21220/801076
 * 注意： 墨西哥 mx 和美东都要配置
 */
export const $httpWithGateway = (
  url: string,
  data = {},
  config?: IRequestConfig
) => {
  const externalChannel = getExternalChannel()
  return ax.post(url, {
    externalChannel,
    ...data,
  }, {
    baseURL: getBaseURL(
      process.env['VUE_APP_API_GATEWAY'],
      url
    ),
    ...config,
  })
}

export interface IRequestConfig {
  [key: string]: string | number | any
}
